<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.ACTIVITY.6 — Actividad: the account's security events
// (sign-ins and refusals with their reason, configuration changes, domains,
// 2FA resets, emergency access) with a CSV export.
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const { overview } = useKaptionSecurity();
const { formatWhen, eventText, eventWho, reasonText } =
  useKaptionSecurityText(overview);

const events = ref([]);
const page = ref(1);
const hasMore = ref(false);
const loading = ref(false);
const filter = ref('');
const exporting = ref(false);

const params = () => (filter.value === 'failure' ? { outcome: 'failure' } : {});

const load = async (reset = true) => {
  loading.value = true;
  try {
    const next = reset ? 1 : page.value + 1;
    const { data } = await KaptionSecurityAPI.events({
      ...params(),
      page: next,
    });
    events.value = reset ? data.events : [...events.value, ...data.events];
    page.value = next;
    hasMore.value = data.has_more;
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    loading.value = false;
  }
};

const exportCsv = async () => {
  exporting.value = true;
  try {
    const response = await KaptionSecurityAPI.exportEvents(params());
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `security-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    exporting.value = false;
  }
};

onMounted(() => load());
watch(filter, () => load());
</script>

<template>
  <div class="flex flex-col gap-11">
    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.ACTIVITY.TITLE')"
      :description="t('KAPTION_SECURITY.ACTIVITY.DESC')"
    >
      <template #actions>
        <select
          v-model="filter"
          class="h-8 px-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :aria-label="t('KAPTION_SECURITY.ACTIVITY.TITLE')"
        >
          <option value="">
            {{ t('KAPTION_SECURITY.ACTIVITY.FILTER_ALL') }}
          </option>
          <option value="failure">
            {{ t('KAPTION_SECURITY.ACTIVITY.FILTER_FAILURES') }}
          </option>
        </select>
        <Button
          sm
          faded
          type="button"
          icon="i-lucide-download"
          :label="t('KAPTION_SECURITY.ACTIVITY.EXPORT')"
          :is-loading="exporting"
          data-testid="ks-export"
          @click="exportCsv"
        />
      </template>

      <div v-if="loading && !events.length" class="flex justify-center py-10">
        <Spinner />
      </div>
      <p v-else-if="!events.length" class="m-0 text-sm text-n-slate-11">
        {{ t('KAPTION_SECURITY.ACTIVITY.EMPTY') }}
      </p>
      <div v-else class="flex flex-col" data-testid="ks-activity-list">
        <div
          class="hidden sm:grid grid-cols-[130px_minmax(0,1fr)_160px] gap-4 pb-2 text-xs font-medium uppercase text-n-slate-11"
        >
          <span>{{ t('KAPTION_SECURITY.ACTIVITY.HEAD_WHEN') }}</span>
          <span>{{ t('KAPTION_SECURITY.ACTIVITY.HEAD_WHAT') }}</span>
          <span class="text-right">{{
            t('KAPTION_SECURITY.ACTIVITY.HEAD_WHO')
          }}</span>
        </div>
        <div
          v-for="event in events"
          :key="event.id"
          class="grid grid-cols-[110px_minmax(0,1fr)] sm:grid-cols-[130px_minmax(0,1fr)_160px] gap-x-4 gap-y-1 py-2.5 text-sm border-t border-n-weak"
        >
          <span class="text-n-slate-11">{{
            formatWhen(event.created_at)
          }}</span>
          <span class="flex flex-col gap-0.5">
            <span class="flex items-start gap-1.5 text-n-slate-12">
              <span
                v-if="event.outcome === 'failure'"
                class="mt-1 i-lucide-circle-x size-3.5 shrink-0 text-n-ruby-11"
                aria-hidden="true"
              />
              {{ eventText(event) }}
            </span>
            <span
              v-if="event.reason_code && event.reason_code.startsWith('SSO-')"
              class="text-[13px] text-n-slate-11"
            >
              {{ reasonText(event.reason_code) }}
            </span>
            <span v-if="event.ip" class="text-xs text-n-slate-10">{{
              event.ip
            }}</span>
          </span>
          <span
            class="col-start-2 sm:col-start-auto text-n-slate-11 sm:text-right"
            >{{ eventWho(event) }}</span
          >
        </div>
        <div v-if="hasMore" class="flex justify-center pt-4">
          <Button
            faded
            sm
            type="button"
            :label="t('KAPTION_SECURITY.ACTIVITY.LOAD_MORE')"
            :is-loading="loading"
            @click="load(false)"
          />
        </div>
      </div>
    </KaptionSecuritySection>
  </div>
</template>
